import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Target, Loader2 } from "lucide-react";
import { US_STATES } from "@/lib/us-states";

const BUDGET_OPTIONS = [
  { value: "under_100k", label: "Under $100K annual budget" },
  { value: "100k_1m", label: "$100K – $1M annual budget" },
  { value: "over_1m", label: "Over $1M annual budget" },
];

const FOCUS_OPTIONS = [
  "Education", "Health", "Arts", "Community", "Environment", "Housing",
  "Youth", "Business", "Technology", "Research", "Historic", "Media",
];

export default function OrgProfilePanel() {
  const { user, setUser } = useAuth();
  const initial = user?.org_profile || {};
  const [form, setForm] = useState({
    mission: initial.mission || "",
    focus_areas: initial.focus_areas || [],
    service_geography: initial.service_geography || "",
    annual_budget: initial.annual_budget || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleFocus = (area) => {
    const has = form.focus_areas.includes(area);
    setForm({
      ...form,
      focus_areas: has ? form.focus_areas.filter((a) => a !== area) : [...form.focus_areas, area],
    });
  };

  const save = async () => {
    if (form.focus_areas.length === 0 && !form.mission.trim()) {
      toast.error("Add a mission or pick at least one focus area");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch("/auth/profile", form);
      setUser(res.data);
      localStorage.setItem("gf_user", JSON.stringify(res.data));
      toast.success("Profile saved — grant matching will use this");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E6E4DF] rounded-lg p-8" data-testid="org-profile-panel">
      <div className="flex items-start gap-3 mb-2">
        <Target className="w-5 h-5 text-[#1C3F35] mt-0.5"/>
        <div>
          <h2 className="font-heading text-xl font-medium">Grant matching profile</h2>
          <p className="text-sm text-[#5E5E5E] mt-1 max-w-md">
            Tell us about your work so we can rank grants by fit. Pro & Enterprise plans see match scores on every grant.
          </p>
        </div>
      </div>

      <div className="space-y-5 mt-6">
        <div>
          <Label>Mission (one paragraph)</Label>
          <Textarea
            rows={3}
            value={form.mission}
            onChange={(e)=>setForm({...form, mission: e.target.value})}
            data-testid="profile-mission"
            className="mt-2 bg-white border-[#E6E4DF]"
            placeholder="We build affordable housing and workforce programs for immigrant families in the Bay Area."
          />
        </div>

        <div>
          <Label>Focus areas</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-focus-grid">
            {FOCUS_OPTIONS.map((f) => {
              const active = form.focus_areas.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  onClick={()=>toggleFocus(f)}
                  data-testid={`focus-${f}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    active
                      ? "bg-[#1C3F35] text-white border-[#1C3F35]"
                      : "bg-white text-[#1A1A1A] border-[#E6E4DF] hover:bg-[#E8ECEB]"
                  }`}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Service geography</Label>
            <Select
              value={form.service_geography || "national"}
              onValueChange={(v)=>setForm({...form, service_geography: v === "national" ? "" : v})}
            >
              <SelectTrigger data-testid="profile-geo" className="mt-2 bg-white border-[#E6E4DF]">
                <SelectValue placeholder="Where you serve"/>
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="national">Nationwide</SelectItem>
                {US_STATES.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Annual budget</Label>
            <Select
              value={form.annual_budget}
              onValueChange={(v)=>setForm({...form, annual_budget: v})}
            >
              <SelectTrigger data-testid="profile-budget" className="mt-2 bg-white border-[#E6E4DF]">
                <SelectValue placeholder="Budget size"/>
              </SelectTrigger>
              <SelectContent>
                {BUDGET_OPTIONS.map((b) => (
                  <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="profile-save"
            className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save profile"}
          </Button>
        </div>
      </div>
    </div>
  );
}
